import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { useResultHandler } from "../../../hooks/useResultHandler";
import { createNews as createNewsRequest } from "../api/news.api";
import type { NewsSubmitRequest } from "../types/news.types";

interface UseCreateNewsReturn {
  loading: boolean;
  error: string | null;
  success: boolean;
  createNews: (data: NewsSubmitRequest) => Promise<void>;
  reset: () => void;
}

export const useCreateNews = (): UseCreateNewsReturn => {
  const { t } = useTranslation();
  const { handleResult } = useResultHandler();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const createNews = useCallback(
    async (data: NewsSubmitRequest) => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(false);

        const response = await createNewsRequest(data);
        const isSuccess = handleResult(response, {
          successMessage: t("news.news_created_successfully"),
          showErrorToast: false,
        });

        if (isSuccess) {
          setSuccess(true);
          return;
        }

        setError(response.user_message || response.message || "Failed to create news");
      } catch (caughtError: unknown) {
        setError(getApiErrorMessage(caughtError, "Failed to create news"));
      } finally {
        setLoading(false);
      }
    },
    [handleResult, t],
  );

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setSuccess(false);
  }, []);

  return { loading, error, success, createNews, reset };
};
